import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Users, AlertTriangle, Clock, Link2, Repeat, RefreshCw, Lock, Search,
  LayoutGrid, User, Building2, Package, Briefcase, ListChecks,
  Check, Pencil, MoreHorizontal, MoonStar, Trash2, GripVertical, Wand2, Loader2,
  Plus, ChevronDown, ChevronRight,
} from "lucide-react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, useDroppable } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import CompleteReminderDialog from "@/components/reminders/CompleteReminderDialog";
import { EditReminderDialog } from "@/components/RemindersPanel";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { fmtRelative } from "@/lib/format";
import DigestPreviewModal from "@/components/DigestPreviewModal";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useWorkspacePerms } from "@/lib/useWorkspacePerms";

/**
 * Feb 2026 · Team Reminders Pinboard
 *
 * A live board that groups every visible open reminder in the workspace
 * by a dimension the viewer picks (Owner / Creator / Delegate / Client /
 * Brand / Project / Task / Priority / Due window). Workspace owners &
 * admins see every reminder (private included) — everyone else is
 * filtered server-side to what they own, were delegated, or share a
 * project/brand/client/department with.
 *
 * Pinboard endpoint returns fully enriched rows; the UI does the
 * grouping so switching views is instant.
 */

const PRI_CHIP = {
  low: "bg-slate-100 text-slate-700 border-slate-300",
  normal: "bg-sky-50 text-sky-700 border-sky-300",
  high: "bg-amber-50 text-amber-700 border-amber-300",
  urgent: "bg-rose-50 text-rose-700 border-rose-300",
};

const GROUP_OPTIONS = [
  { key: "owner", label: "Owner", Icon: User },
  { key: "creator", label: "Creator", Icon: User },
  { key: "delegate", label: "Delegate", Icon: Users },
  { key: "client", label: "Client", Icon: Building2 },
  { key: "brand", label: "Brand", Icon: Package },
  { key: "project", label: "Project", Icon: Briefcase },
  { key: "priority", label: "Priority", Icon: AlertTriangle },
  { key: "due", label: "Due window", Icon: Clock },
];

const PRIORITY_ORDER = { urgent: 0, high: 1, normal: 2, low: 3 };

// R268~38 · Feb 2026 — Module-level 60s cache for workspace members so
// re-opening the shared Edit dialog is instant on the second try. Was
// noticeably slow because RemindersPanel refetches on every render.
const _MEMBERS_CACHE = new Map();
async function _cachedMembers(wsId) {
  if (!wsId) return [];
  const cached = _MEMBERS_CACHE.get(wsId);
  if (cached && Date.now() - cached.ts < 60_000) return cached.data;
  try {
    const { data } = await api.get(`/workspaces/${wsId}/members`);
    const rows = Array.isArray(data) ? data : (data?.items || []);
    _MEMBERS_CACHE.set(wsId, { ts: Date.now(), data: rows });
    return rows;
  } catch { return []; }
}

// R268~38 · Wrapper — fetches the reminder + members, then forwards to
// the shared EditReminderDialog exported from RemindersPanel. Keeps
// the Team Reminders and header popup on the same UX for consistency.
function TeamRemindersEditWrapper({ rid, wsId, currentUserId, onClose, onSaved }) {
  const [rem, setRem] = useState(null);
  const [members, setMembers] = useState([]);
  useEffect(() => {
    let cancelled = false;
    api.get(`/reminders/${rid}`).then(({ data }) => {
      if (!cancelled) setRem(data);
    }).catch((e) => { toast.error(apiErrorMessage(e, "Failed to load reminder")); onClose(); });
    _cachedMembers(wsId).then((rows) => { if (!cancelled) setMembers(rows); });
    return () => { cancelled = true; };
  }, [rid, wsId, onClose]);
  if (!rem) return null;
  return (
    <EditReminderDialog
      reminder={rem}
      members={members}
      currentUserId={currentUserId}
      workspaceId={wsId}
      onClose={onClose}
      onSaved={onSaved}
    />
  );
}

// R268~29 · Feb 2026 — Age helper for the "N days old" sticker. Returns
// a compact human string ("just now", "3h", "12d", "2mo") based on the
// diff vs now. Null when there's no created_at.
const ageChipText = (createdAt) => {
  if (!createdAt) return null;
  const then = new Date(createdAt).getTime();
  const now = Date.now();
  if (!Number.isFinite(then)) return null;
  const diffMs = now - then;
  if (diffMs < 60_000) return "just now";
  const mins = Math.round(diffMs / 60_000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months}mo`;
  return `${Math.round(months / 12)}y`;
};

function Initials({ name, avatar, size = 8 }) {
  const px = size * 4;
  if (avatar) return <img src={avatar} alt={name || ""} style={{ width: px, height: px }} className="rounded-full object-cover" />;
  const initials = (name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  return (
    <span
      style={{ width: px, height: px }}
      className="rounded-full bg-primary/15 text-primary text-[11px] font-semibold flex items-center justify-center"
    >
      {initials}
    </span>
  );
}

function dueWindowFor(r) {
  const now = new Date();
  const when = r.remind_at || r.due_at;
  if (!when) return "Someday";
  const dt = new Date(when);
  if (Number.isNaN(dt.getTime())) return "Someday";
  const diffMs = dt.getTime() - now.getTime();
  if (diffMs < 0) return "Overdue";
  const oneDay = 86_400_000;
  if (diffMs < oneDay) return "Today";
  if (diffMs < oneDay * 2) return "Tomorrow";
  if (diffMs < oneDay * 7) return "This week";
  if (diffMs < oneDay * 30) return "This month";
  return "Later";
}
const DUE_WINDOW_ORDER = ["Overdue", "Today", "Tomorrow", "This week", "This month", "Later", "Someday"];

function bucketKeys(r, groupBy) {
  // Returns { id, label } identifying the pinboard column for the row
  switch (groupBy) {
    case "owner":
      return { id: r.owner_id || "unknown", label: r.owner_name || "Unassigned" };
    case "creator":
      return { id: r.owner_id || "unknown", label: r.owner_name || "Unknown" };
    case "delegate":
      return r.delegated_to
        ? { id: r.delegated_to, label: r.delegated_to_name || "Delegate" }
        : { id: "__none__", label: "Not delegated" };
    case "client":
      return r.client_id
        ? { id: r.client_id, label: r.client_name || r.client_id }
        : { id: "__none__", label: "No client" };
    case "brand":
      return r.brand_id
        ? { id: r.brand_id, label: r.brand_name || r.brand_id }
        : { id: "__none__", label: "No brand" };
    case "project":
      return r.project_id
        ? { id: r.project_id, label: r.project_name || r.project_id }
        : { id: "__none__", label: "No project" };
    case "priority":
      return { id: r.priority || "normal", label: (r.priority || "normal").toUpperCase() };
    case "due":
      return { id: dueWindowFor(r), label: dueWindowFor(r) };
    default:
      return { id: r.owner_id || "unknown", label: r.owner_name || "Unassigned" };
  }
}

export default function TeamReminders() {
  const { activeWorkspaceId, user } = useAuth();
  const { isAdminRole } = useWorkspacePerms();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  // R268hhh · Feb 2026 — Admin-only duplicate-cleanup dialog. Users with
  // an admin role see a "Fix duplicates" button in the header; clicking
  // opens a two-step wizard (preview counts → confirm) that calls the
  // POST /reminders/cleanup endpoint so non-technical admins can clear
  // phantom recurring rows without running curl.
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [cleanupBusy, setCleanupBusy] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);
  const [filter, setFilter] = useState("open"); // open | overdue | all | mine
  const [groupBy, setGroupBy] = useState(() => {
    try { return localStorage.getItem("co_team_reminders_group") || "owner"; } catch { return "owner"; }
  });
  const [query, setQuery] = useState("");
  const [sp] = useSearchParams();
  const [digestPreviewOpen, setDigestPreviewOpen] = useState(sp.get("preview") === "1");
  const [docsByUser, setDocsByUser] = useState({});
  // R268~28 · Feb 2026 — Sort options for items inside each bucket.
  // Always defaults to "manual" on load (drag-persisted order shines
  // through), so users don't get stuck on a stale non-manual pick from
  // a previous session (R268~35). The choice is not persisted anymore.
  const [sortBy, setSortBy] = useState("manual");
  // R268~22 · Feb 2026 — Per-bucket expand/collapse. Key format:
  // `<groupBy>::<bucket.id>` so switching Group by doesn't leak state
  // across dimensions. Persisted in localStorage so a manager's
  // preferred collapsed view survives refreshes and route changes.
  const [collapsedBuckets, setCollapsedBuckets] = useState(() => {
    try {
      const raw = localStorage.getItem("co_team_reminders_collapsed");
      return new Set(raw ? JSON.parse(raw) : []);
    } catch { return new Set(); }
  });
  useEffect(() => {
    try { localStorage.setItem("co_team_reminders_collapsed", JSON.stringify([...collapsedBuckets])); } catch {}
  }, [collapsedBuckets]);
  const toggleBucketCollapsed = (bkey) => {
    setCollapsedBuckets((prev) => {
      const next = new Set(prev);
      if (next.has(bkey)) next.delete(bkey); else next.add(bkey);
      return next;
    });
  };
  // R155 · Feb 2026 — Inline edit / complete from Team Reminders board.
  const [editingId, setEditingId] = useState(null);
  // R268~1 · Feb 2026 — "Add reminder" flow. `showNew` opens a blank
  // dialog; `newPreset` scopes it to a specific bucket (owner, client,
  // project, brand, priority, delegate) so the per-card quick-add works.
  const [showNew, setShowNew] = useState(false);
  const [newPreset, setNewPreset] = useState(null);
  // R268~3 · Feb 2026 — Every mark-done opens a dialog with a mandatory
  // response note. The bucket click sets a full reminder object so the
  // dialog can show its title.
  const [completingRem, setCompletingRem] = useState(null);

  // R138i · Feb 2026 — Drag-to-reorder within any bucket. We persist the
  // whole ordered id list per user so any grouping shows their order.
  const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const persistOrder = async (ids, forAll = false) => {
    if (!activeWorkspaceId) return;
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/reminders/reorder`, { ids, for_all: forAll });
      if (forAll) toast.success("Order synced to team as workspace default");
    } catch (e) { toast.error(apiErrorMessage(e, "Reorder failed")); }
  };
  // R268~35 · Feb 2026 — Admin action: push my current drag order to
  // the whole team as the workspace default fallback.
  const syncOrderToTeam = () => {
    if (!isAdminRole) return;
    const ids = rows.map((r) => r.id);
    if (!ids.length) { toast.error("Nothing to sync yet"); return; }
    if (!window.confirm(`Sync this order to every teammate as the workspace default (${ids.length} reminders)? Members who have their own manual order will still see their own.`)) return;
    persistOrder(ids, true);
  };
  const handleReorder = (bucket, event) => {
    const { active, over } = event || {};
    if (!over || active.id === over.id) return;
    // Reorder within the bucket first…
    const bucketIds = bucket.items.map((r) => r.id);
    const from = bucketIds.indexOf(active.id);
    const to = bucketIds.indexOf(over.id);
    if (from === -1 || to === -1) return;
    const nextBucketIds = arrayMove(bucketIds, from, to);
    // …then fold that back into the global rows array so grouping stays
    // stable across other buckets. We update the rows state so the UI
    // reflects the change immediately.
    const nextRows = [...rows];
    const bucketSet = new Set(bucketIds);
    let cursor = 0;
    for (let i = 0; i < nextRows.length; i++) {
      if (bucketSet.has(nextRows[i].id)) {
        const nextId = nextBucketIds[cursor++];
        nextRows[i] = rows.find((r) => r.id === nextId);
      }
    }
    setRows(nextRows.map((r, i) => ({ ...r, _rank: i })));
    persistOrder(nextRows.map((r) => r.id));
  };
  // R268~39 · Feb 2026 — Cross-bucket drag: when the user drops a
  // reminder onto a DIFFERENT bucket's drop-zone we treat it as
  // "re-delegate" (owner/delegate groupings) or "re-scope" (client /
  // brand / project / priority groupings). Backend rules still apply:
  // only admins + owner + current delegatee can re-delegate someone
  // else's reminder — the server will reject with a 403 otherwise and
  // we roll back the optimistic local update.
  const handleCrossBucketDrop = async (reminderId, targetBucketId) => {
    const r = rows.find((x) => x.id === reminderId);
    if (!r) return;
    // Compute the patch body based on the current Group by.
    const isNone = targetBucketId === "__none__";
    const patch = {};
    let optimistic = null;
    if (groupBy === "delegate") {
      patch.delegated_to = isNone ? null : targetBucketId;
      optimistic = { delegated_to: patch.delegated_to };
    } else if (groupBy === "owner") {
      // Re-owning isn't supported by the backend as a plain PATCH —
      // fall back to "delegate to new owner" which lands the reminder
      // in that user's list.
      patch.delegated_to = isNone ? null : targetBucketId;
      optimistic = { delegated_to: patch.delegated_to };
    } else if (groupBy === "client") {
      patch.client_id = isNone ? null : targetBucketId;
      optimistic = { client_id: patch.client_id, brand_id: null, project_id: null };
    } else if (groupBy === "brand") {
      patch.brand_id = isNone ? null : targetBucketId;
      optimistic = { brand_id: patch.brand_id };
    } else if (groupBy === "project") {
      patch.project_id = isNone ? null : targetBucketId;
      optimistic = { project_id: patch.project_id };
    } else if (groupBy === "priority") {
      patch.priority = targetBucketId;
      optimistic = { priority: patch.priority };
    } else {
      return; // creator / task / due — no meaningful cross-bucket action
    }
    // Optimistic UI so the card jumps immediately.
    const before = rows;
    setRows(rows.map((x) => x.id === reminderId ? { ...x, ...optimistic } : x));
    try {
      await api.patch(`/reminders/${reminderId}`, patch);
      toast.success("Reminder moved");
      load();
    } catch (e) {
      setRows(before);   // rollback
      toast.error(apiErrorMessage(e, "Move failed"));
    }
  };

  useEffect(() => {
    try { localStorage.setItem("co_team_reminders_group", groupBy); } catch { /* noop */ }
  }, [groupBy]);

  const load = async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      // `all` and `done` need done rows too. Backend enforces visibility.
      const wantAll = filter === "all" || filter === "done";
      // R138i · Feb 2026 — 45s hard timeout so the loader never sticks
      // forever when the endpoint hangs (was the Portalwiz prod hang).
      const { data } = await api.get(
        `/workspaces/${activeWorkspaceId}/reminders/pinboard`,
        { params: { status: wantAll ? "all" : "open" }, timeout: 45000 });
      // R138i · Feb 2026 — Attach _rank from the server-provided order so the
      // bucket sort honours the user's custom drag order (persisted in
      // user_reminder_order). Rows without a rank fall back to overdue/remind_at.
      const incoming = Array.isArray(data?.rows) ? data.rows : [];
      setRows(incoming.map((r, i) => ({ ...r, _rank: i })));
      try {
        const { data: comp } = await api.get(
          `/workspaces/${activeWorkspaceId}/employee-documents/compliance`,
          { timeout: 20000 });
        const by = {};
        for (const r of (comp?.rows || [])) by[r.user_id] = r;
        setDocsByUser(by);
      } catch { /* HR-gated — silent fail */ }
    } catch (e) {
      // Feb 2026 — Show the exact backend message + status so the user
      // can share it verbatim (the old generic toast made prod triage
      // guesswork). Format: "Team Reminders: 500 · reminders_pinboard timed out"
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.response?.data?.message
        || e?.message || "Unknown error";
      const short = String(detail).slice(0, 120);
      const msg = e?.code === "ECONNABORTED"
        ? "Team Reminders timed out — please retry or contact support if this repeats."
        : `Team Reminders: ${status ? status + " · " : ""}${short}`;
      toast.error(msg);
      // eslint-disable-next-line no-console
      console.error("[team-reminders] load failed", { status, detail, err: e });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspaceId, filter]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter === "overdue" && !r._overdue) return false;
      if (filter === "mine" && r.owner_id !== user?.id && r.delegated_to !== user?.id) return false;
      if (filter === "done" && r.status !== "done") return false;
      if (filter !== "done" && filter !== "all" && r.status === "done") return false;
      if (!q) return true;
      const hay = [
        r.title, r.note, r.owner_name, r.delegated_to_name,
        r.client_name, r.brand_name, r.project_name,
        r.linked_entity?.name, r.priority,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [rows, query, filter, user?.id]);

  const buckets = useMemo(() => {
    const map = new Map();
    for (const r of filtered) {
      const b = bucketKeys(r, groupBy);
      const bkey = b.id;
      if (!map.has(bkey)) {
        map.set(bkey, { id: bkey, label: b.label, items: [], overdue: 0 });
      }
      const bucket = map.get(bkey);
      bucket.items.push(r);
      if (r._overdue) bucket.overdue += 1;
    }
    let arr = Array.from(map.values());
    // Sort buckets by preferred order per group-by, then by overdue count.
    if (groupBy === "priority") {
      arr.sort((a, b) => (PRIORITY_ORDER[a.id] ?? 9) - (PRIORITY_ORDER[b.id] ?? 9));
    } else if (groupBy === "due") {
      arr.sort((a, b) => DUE_WINDOW_ORDER.indexOf(a.id) - DUE_WINDOW_ORDER.indexOf(b.id));
    } else {
      arr.sort((a, b) => {
        const noneA = a.id === "__none__" ? 1 : 0;
        const noneB = b.id === "__none__" ? 1 : 0;
        if (noneA !== noneB) return noneA - noneB;
        if (a.overdue !== b.overdue) return b.overdue - a.overdue;
        return (a.label || "").localeCompare(b.label || "");
      });
    }
    // Sort items in each bucket. Default = user's manual/drag order
    // ("manual"). Any other sortBy value overrides _rank so managers
    // can quickly re-scan the whole board by age/priority/etc.
    for (const b of arr) {
      b.items.sort((x, y) => {
        if (sortBy === "priority") {
          const p = (PRIORITY_ORDER[x.priority] ?? 9) - (PRIORITY_ORDER[y.priority] ?? 9);
          if (p !== 0) return p;
        }
        if (sortBy === "due_soon" || sortBy === "due_late") {
          const wx = x.remind_at || x.due_at || "";
          const wy = y.remind_at || y.due_at || "";
          if (wx && !wy) return -1;
          if (!wx && wy) return 1;
          if (wx && wy && wx !== wy) return sortBy === "due_soon" ? wx.localeCompare(wy) : wy.localeCompare(wx);
        }
        if (sortBy === "newest" || sortBy === "oldest") {
          const cx = x.created_at || x.remind_at || "";
          const cy = y.created_at || y.remind_at || "";
          if (cx && cy && cx !== cy) return sortBy === "newest" ? cy.localeCompare(cx) : cx.localeCompare(cy);
        }
        if (sortBy === "title") {
          const c = (x.title || "").localeCompare(y.title || "");
          if (c !== 0) return c;
        }
        // Manual (default) — respect drag order first.
        const rx = x._rank, ry = y._rank;
        if (rx != null && ry != null && rx !== ry) return rx - ry;
        if (x._overdue !== y._overdue) return y._overdue ? 1 : -1;
        const wx = x.remind_at || x.due_at || "";
        const wy = y.remind_at || y.due_at || "";
        if (wx && wy && wx !== wy) return wx.localeCompare(wy);
        return (PRIORITY_ORDER[x.priority] ?? 9) - (PRIORITY_ORDER[y.priority] ?? 9);
      });
    }
    return arr;
  }, [filtered, groupBy, sortBy]);

  // R268~32 · Feb 2026 — Bulk-complete selection state (admin-only
  // multi-select). Reset when the filter or Group by changes so an
  // admin doesn't accidentally close reminders that scrolled away.
  const [selected, setSelected] = useState(() => new Set());
  useEffect(() => { setSelected(new Set()); }, [filter, groupBy]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkNote, setBulkNote] = useState("");
  const [showBulkDlg, setShowBulkDlg] = useState(false);
  const toggleSelected = useCallback((rid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(rid)) next.delete(rid); else next.add(rid);
      return next;
    });
  }, []);
  const bulkComplete = async () => {
    const note = bulkNote.trim();
    if (!note) { toast.error("Add a short response note before closing"); return; }
    setBulkBusy(true);
    let ok = 0, fail = 0;
    for (const rid of [...selected]) {
      try {
        await api.post(`/reminders/${rid}/complete`, { response_note: note });
        ok += 1;
      } catch (e) { fail += 1; }
    }
    setBulkBusy(false);
    setSelected(new Set());
    setShowBulkDlg(false);
    setBulkNote("");
    if (ok) toast.success(`Closed ${ok} reminder${ok === 1 ? "" : "s"}${fail ? ` · ${fail} failed` : ""}`);
    else if (fail) toast.error(`Failed to close ${fail} reminder${fail === 1 ? "" : "s"}`);
    load();
  };
  // R268~33 · Feb 2026 — Stale-days threshold (age chip flips orange
  // once a reminder crosses this many days without being resolved).
  // Persisted per workspace in localStorage so every user in the same
  // browser session sees a consistent tone. Overdue always wins (red).
  const [staleDays, setStaleDays] = useState(() => {
    try {
      const raw = parseInt(localStorage.getItem(`co_team_reminders_stale_days_${activeWorkspaceId || "_"}`) || "7", 10);
      return Number.isFinite(raw) && raw > 0 ? raw : 7;
    } catch { return 7; }
  });
  useEffect(() => {
    try { localStorage.setItem(`co_team_reminders_stale_days_${activeWorkspaceId || "_"}`, String(staleDays)); } catch {}
  }, [staleDays, activeWorkspaceId]);
  const [showStaleSetting, setShowStaleSetting] = useState(false);

  const totals = useMemo(() => {
    const open = rows.filter((r) => r.status === "open").length;
    const overdue = rows.filter((r) => r._overdue).length;
    const owners = new Set(rows.map((r) => r.owner_id).filter(Boolean)).size;
    return { open, overdue, owners };
  }, [rows]);

  return (
    <div className="p-3 sm:p-6 space-y-3 sm:space-y-5" data-testid="team-reminders-page">
      {/* Header */}
      <div className="flex items-start sm:items-center justify-between gap-2 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide">
            <LayoutGrid className="w-3 h-3" /> Reminders pinboard
          </div>
          <h1 className="text-xl sm:text-3xl font-bold mt-0.5 tracking-tight">Team Reminders</h1>
          <p className="hidden sm:block text-sm text-muted-foreground mt-1">
            Group by owner, delegate, client, brand, project, task, priority or due window. Nothing quietly slips.
          </p>
        </div>
        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
          <button
            type="button"
            onClick={() => { setNewPreset(null); setShowNew(true); }}
            className="text-[11px] sm:text-xs px-2 sm:px-3 py-1.5 border border-primary bg-primary text-primary-foreground hover:opacity-90 flex items-center gap-1"
            data-testid="team-reminders-new-btn"
            title="Add a new reminder"
          >
            <Plus className="w-3.5 h-3.5" /> <span className="hidden xs:inline sm:inline">New</span><span className="hidden sm:inline"> reminder</span>
          </button>
          {isAdminRole && (
            <button
              type="button"
              onClick={() => { setCleanupOpen(true); setCleanupPreview(null); setCleanupResult(null); }}
              className="text-[11px] sm:text-xs px-2 sm:px-3 py-1.5 border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-500/10 text-amber-800 dark:text-amber-300 hover:bg-amber-100 flex items-center gap-1"
              data-testid="team-reminders-cleanup-btn"
              title="Preview + soft-delete duplicate open reminders (admin only)"
            >
              <Wand2 className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Fix duplicates</span>
            </button>
          )}
          <button
            type="button"
            onClick={load}
            className="text-[11px] sm:text-xs px-2 sm:px-3 py-1.5 border border-border hover:bg-muted flex items-center gap-1"
            data-testid="team-reminders-refresh"
            title="Refresh reminders list"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> <span className="hidden sm:inline">Refresh</span>
          </button>
          {/* R268~33 · Feb 2026 — Workspace-owner setting: stale-days
              threshold (age chip flips amber past this many days). */}
          {isAdminRole && (
            <button
              type="button"
              onClick={syncOrderToTeam}
              className="text-[11px] sm:text-xs px-2 sm:px-3 py-1.5 border border-border hover:bg-muted flex items-center gap-1"
              data-testid="team-reminders-sync-order-btn"
              title="Save my current drag order as the workspace default so teammates inherit it"
            >
              <GripVertical className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Sync order</span>
            </button>
          )}
          {isAdminRole && (
            <button
              type="button"
              onClick={() => setShowStaleSetting((v) => !v)}
              className="text-[11px] sm:text-xs px-2 sm:px-3 py-1.5 border border-border hover:bg-muted flex items-center gap-1"
              data-testid="team-reminders-stale-setting-btn"
              title={`Age sticker turns amber after ${staleDays} days`}
            >
              <Clock className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Stale: {staleDays}d</span>
            </button>
          )}
        </div>
      </div>

      {/* Stale threshold quick-setting popover */}
      {showStaleSetting && isAdminRole && (
        <div className="border border-border bg-amber-50 dark:bg-amber-500/10 px-3 py-2 flex items-center gap-3 flex-wrap" data-testid="team-reminders-stale-panel">
          <Clock className="w-4 h-4 text-amber-700 shrink-0" />
          <label className="text-[11px] text-amber-900 dark:text-amber-100 font-medium">
            Turn age sticker amber after
            <input
              type="number"
              min={1}
              max={365}
              value={staleDays}
              onChange={(e) => setStaleDays(Math.max(1, parseInt(e.target.value || "7", 10) || 7))}
              className="ml-2 w-16 h-7 px-1.5 border border-amber-300 bg-background text-xs text-center"
              data-testid="team-reminders-stale-days"
            />
            <span className="ml-1">days</span>
          </label>
          <span className="text-[10px] text-amber-800/70 dark:text-amber-100/70">
            (overdue always wins with red · saved to this browser)
          </span>
          <button
            type="button"
            onClick={() => setShowStaleSetting(false)}
            className="ml-auto text-[11px] text-amber-900 dark:text-amber-100 underline"
            data-testid="team-reminders-stale-close"
          >Done</button>
        </div>
      )}

      {/* Totals — compact 3-col strip on mobile, richer cards on desktop. */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        <SummaryCard label="Owners with open items" value={totals.owners} />
        <SummaryCard label="Open reminders" value={totals.open} accent="sky" />
        <SummaryCard label="Overdue" value={totals.overdue} accent="rose" />
      </div>

      {/* Controls — compact multi-row layout that folds cleanly on
          mobile (Group by → native <select>, filter chips + search
          stack in their own rows). */}
      <div className="border border-border bg-muted/30 px-2 sm:px-3 py-2 space-y-2">
        {/* Row 1 — Group by (chips on desktop / native select on mobile) + Filter chips */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium mr-1 hidden sm:inline">Group by</span>
          {/* Mobile: native select. Desktop: chips (unchanged). */}
          <select
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value)}
            className="sm:hidden text-[11px] h-8 px-2 border border-border bg-background focus:outline-none focus:border-primary flex-1 min-w-[120px]"
            data-testid="team-reminders-groupby-mobile"
          >
            {GROUP_OPTIONS.map(({ key, label }) => (
              <option key={key} value={key}>{`Group by · ${label}`}</option>
            ))}
          </select>
          <div className="hidden sm:inline-flex flex-wrap gap-1" data-testid="team-reminders-groupby">
            {GROUP_OPTIONS.map(({ key, label, Icon }) => (
              <button
                key={key}
                type="button"
                onClick={() => setGroupBy(key)}
                className={`text-[11px] px-2 py-1 border inline-flex items-center gap-1 transition ${groupBy === key ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:border-primary/60"}`}
                data-testid={`team-groupby-${key}`}
              >
                <Icon className="w-3 h-3" /> {label}
              </button>
            ))}
          </div>
          <div className="flex-1 hidden sm:block" />
          <div className="inline-flex gap-0.5 border border-border overflow-x-auto max-w-full" data-testid="team-reminders-filter">
            {[
              { k: "open", l: "Open" },
              { k: "overdue", l: "Overdue" },
              { k: "mine", l: "Mine" },
              { k: "done", l: "Done" },
              { k: "all", l: "All" },
            ].map(({ k, l }) => (
              <button
                key={k}
                type="button"
                onClick={() => setFilter(k)}
                className={`text-[11px] px-2 py-1 whitespace-nowrap transition ${filter === k ? (k === "overdue" ? "bg-rose-500 text-white" : k === "done" ? "bg-emerald-500 text-white" : "bg-primary text-primary-foreground") : "bg-background text-muted-foreground hover:text-foreground"}`}
                data-testid={`team-filter-${k}`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
        {/* Row 2 — Search + Sort + Expand/Collapse. */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[160px]">
            <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search title, note, entity…"
              className="text-[11px] pl-6 pr-2 py-1 h-8 bg-background border border-border w-full focus:outline-none focus:border-primary"
              data-testid="team-reminders-search"
            />
          </div>
          <label className="text-[10px] text-muted-foreground uppercase tracking-widest hidden sm:inline">Sort</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="text-[11px] px-2 py-1 h-8 bg-background border border-border focus:outline-none focus:border-primary"
            data-testid="team-reminders-sortby"
            title="Sort items inside each bucket"
          >
            <option value="manual">Manual (drag)</option>
            <option value="priority">Priority · high → low</option>
            <option value="due_soon">Due date · soonest</option>
            <option value="due_late">Due date · latest</option>
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="title">Title A → Z</option>
          </select>
          <div className="inline-flex gap-0 border border-border" data-testid="team-reminders-expandcollapse">
            <button
              type="button"
              onClick={() => setCollapsedBuckets(new Set())}
              className="text-[11px] px-2 py-1 h-8 bg-background text-muted-foreground hover:text-foreground"
              title="Expand every bucket card"
              data-testid="team-reminders-expand-all"
            >
              <span className="hidden sm:inline">Expand all</span>
              <span className="sm:hidden">Open</span>
            </button>
            <button
              type="button"
              onClick={() => setCollapsedBuckets(new Set(buckets.map((b) => `${groupBy}::${b.id}`)))}
              className="text-[11px] px-2 py-1 h-8 bg-background text-muted-foreground hover:text-foreground border-l border-border"
              title="Collapse every bucket card"
              data-testid="team-reminders-collapse-all"
            >
              <span className="hidden sm:inline">Collapse all</span>
              <span className="sm:hidden">Close</span>
            </button>
          </div>
        </div>
      </div>

      {/* Board */}
      {loading && rows.length === 0 && (
        <div className="text-sm text-muted-foreground py-16 text-center">Loading…</div>
      )}
      {!loading && buckets.length === 0 && (
        <div className="text-sm text-muted-foreground py-16 text-center border border-dashed border-border">
          {filter === "overdue" ? "No overdue reminders — nice." : "Nothing to show for this filter."}
        </div>
      )}
      <DndContext
        sensors={dndSensors}
        collisionDetection={closestCenter}
        onDragEnd={(e) => {
          // R268~39 · Feb 2026 — Cross-bucket drop detection. Each
          // bucket registers a droppable with id `bucket-<bid>`; when
          // the drop target is one of those (or a card living in a
          // different bucket), we treat it as a delegate/scope change.
          const { active, over } = e || {};
          if (!over || active.id === over.id) return;
          const findBucket = (rid) => buckets.find((b) => b.items.some((x) => x.id === rid));
          const srcBucket = findBucket(active.id);
          let dstBucketId = null;
          const overId = String(over.id);
          if (overId.startsWith("bucket-")) {
            dstBucketId = overId.slice(7);
          } else {
            const dstBucket = findBucket(over.id);
            dstBucketId = dstBucket?.id ?? null;
          }
          if (srcBucket && dstBucketId && dstBucketId !== srcBucket.id) {
            handleCrossBucketDrop(active.id, dstBucketId);
            return;
          }
          // Same-bucket reorder path.
          if (srcBucket) handleReorder(srcBucket, e);
        }}
      >
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3 sm:gap-4">
        {buckets.map((b) => (
          <PinBucket
            key={`${groupBy}-${b.id}`}
            bucket={b}
            groupBy={groupBy}
            docs={groupBy === "owner" || groupBy === "creator" ? docsByUser[b.id] : null}
            currentUserId={user?.id}
            isAdmin={isAdminRole}
            wsId={activeWorkspaceId}
            selected={selected}
            onToggleSelected={toggleSelected}
            staleDays={staleDays}
            quickPreset={(() => {
              // Precompute preset once per bucket. When `owner` or
              // `creator` bucket is someone else, delegate the new
              // reminder to them so it also lands in their list (R268~29).
              const p = {};
              if ((groupBy === "owner" || groupBy === "creator")
                  && b.id !== "__none__" && b.id !== user?.id) {
                p.delegated_to = b.id;
              }
              if (groupBy === "delegate" && b.id !== "__none__") p.delegated_to = b.id;
              if (groupBy === "client" && b.id !== "__none__") p.client_id = b.id;
              if (groupBy === "brand" && b.id !== "__none__") p.brand_id = b.id;
              if (groupBy === "project" && b.id !== "__none__") p.project_id = b.id;
              if (groupBy === "priority") p.priority = b.id;
              return p;
            })()}
            onQuickCreated={load}
            onQuickAdd={() => {
              // R268~1 · Scope the new reminder to this bucket. Owner /
              // creator buckets for OTHER users delegate to them so the
              // recipient sees the reminder too (R268~29).
              const preset = { bucketLabel: b.label };
              if ((groupBy === "owner" || groupBy === "creator")
                  && b.id !== "__none__" && b.id !== user?.id) {
                preset.delegated_to = b.id;
              }
              if (groupBy === "delegate" && b.id !== "__none__") preset.delegated_to = b.id;
              if (groupBy === "client" && b.id !== "__none__") preset.client_id = b.id;
              if (groupBy === "brand" && b.id !== "__none__") preset.brand_id = b.id;
              if (groupBy === "project" && b.id !== "__none__") preset.project_id = b.id;
              if (groupBy === "priority") preset.priority = b.id;
              setNewPreset(preset);
              setShowNew(true);
            }}
            onComplete={(r) => setCompletingRem(r)}
            onEdit={setEditingId}
            collapsed={collapsedBuckets.has(`${groupBy}::${b.id}`)}
            onToggleCollapsed={() => toggleBucketCollapsed(`${groupBy}::${b.id}`)}
          />
        ))}
      </div>
      </DndContext>

      {/* R268~32 · Feb 2026 — Floating bulk-action bar. Only visible
          when admins have selected at least one open reminder. */}
      {selected.size > 0 && isAdminRole && (
        <div
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 border border-border bg-background shadow-lg flex items-center gap-2 px-3 py-2"
          data-testid="team-reminders-bulk-bar"
        >
          <span className="text-xs font-medium">
            {selected.size} selected
          </span>
          <button
            type="button"
            onClick={() => setSelected(new Set())}
            className="text-[11px] px-2 py-1 border border-border hover:bg-muted"
            data-testid="team-reminders-bulk-clear"
          >Clear</button>
          <button
            type="button"
            onClick={() => setShowBulkDlg(true)}
            className="text-[11px] px-3 py-1 border border-emerald-300 bg-emerald-500 text-white hover:bg-emerald-600 font-medium flex items-center gap-1"
            data-testid="team-reminders-bulk-complete-btn"
          >
            <Check className="w-3 h-3" /> Complete selected
          </button>
        </div>
      )}

      {/* Bulk-complete confirmation dialog */}
      {showBulkDlg && (
        <div className="fixed inset-0 z-[1100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowBulkDlg(false)} data-testid="team-reminders-bulk-dialog">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-background border border-border shadow-2xl">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between bg-muted/40">
              <h3 className="text-sm font-semibold">
                Close {selected.size} reminder{selected.size === 1 ? "" : "s"}
              </h3>
              <button type="button" onClick={() => setShowBulkDlg(false)} className="text-muted-foreground hover:text-foreground text-lg leading-none">×</button>
            </div>
            <div className="p-4 space-y-2 text-xs">
              <p className="text-muted-foreground">
                A response note is applied to every selected reminder so the audit trail stays consistent.
              </p>
              <div>
                <div className="label-eyebrow text-[10px] mb-0.5">Response note (required)</div>
                <textarea
                  value={bulkNote}
                  onChange={(e) => setBulkNote(e.target.value)}
                  rows={3}
                  placeholder="e.g. Sweep close · resolved during standup"
                  className="w-full text-xs px-2 py-1 border border-border bg-background resize-y"
                  data-testid="team-reminders-bulk-note"
                />
              </div>
            </div>
            <div className="px-4 py-3 border-t border-border flex items-center gap-2 bg-muted/20">
              <button type="button" onClick={() => setShowBulkDlg(false)} disabled={bulkBusy} className="text-xs px-3 py-2 border border-border hover:bg-muted mr-auto" data-testid="team-reminders-bulk-cancel">
                Cancel
              </button>
              <button type="button" onClick={bulkComplete} disabled={bulkBusy || !bulkNote.trim()} className="text-xs px-4 py-2 bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 font-medium" data-testid="team-reminders-bulk-confirm">
                {bulkBusy ? "Closing…" : `Close ${selected.size} reminder${selected.size === 1 ? "" : "s"}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {editingId && (
        <TeamRemindersEditWrapper
          rid={editingId}
          wsId={activeWorkspaceId}
          currentUserId={user?.id}
          onClose={() => setEditingId(null)}
          onSaved={() => { setEditingId(null); load(); }}
        />
      )}

      {showNew && (
        <TeamNewReminderDialog
          wsId={activeWorkspaceId}
          preset={newPreset}
          onClose={() => { setShowNew(false); setNewPreset(null); }}
          onCreated={() => { setShowNew(false); setNewPreset(null); load(); }}
        />
      )}

      {completingRem && (
        <CompleteReminderDialog
          reminder={completingRem}
          onClose={() => setCompletingRem(null)}
          onDone={() => { setCompletingRem(null); load(); }}
        />
      )}

      {/* R268hhh · Feb 2026 — Admin-only cleanup wizard */}
      {cleanupOpen && (
        <Dialog open onOpenChange={(o) => !o && setCleanupOpen(false)}>
          <DialogContent className="rounded-none sm:max-w-lg" data-testid="team-reminders-cleanup-dialog">
            <DialogHeader>
              <DialogTitle className="text-base flex items-center gap-2">
                <Wand2 className="w-4 h-4 text-amber-600" /> Fix duplicate reminders
              </DialogTitle>
              <DialogDescription className="text-[11px]">
                Finds phantom copies of the same recurring reminder and soft-deletes the extras,
                keeping the earliest one per chain. Safe to run — nothing is hard-deleted, and you
                can restore any row from the database if needed.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 text-xs">
              {!cleanupResult && !cleanupPreview && (
                <div className="flex flex-col gap-2 pt-1">
                  <div className="text-muted-foreground">
                    Step 1 — <span className="font-semibold text-foreground">Preview</span> the counts
                    without changing anything.
                  </div>
                  <button type="button"
                          className="self-start px-3 py-1.5 border border-border hover:bg-muted flex items-center gap-1.5"
                          data-testid="team-reminders-cleanup-preview"
                          disabled={cleanupBusy}
                          onClick={async () => {
                            if (!activeWorkspaceId) return;
                            setCleanupBusy(true);
                            try {
                              const { data } = await api.post(
                                `/workspaces/${activeWorkspaceId}/reminders/cleanup`,
                                { dry_run: true, min_duplicates: 5 });
                              setCleanupPreview(data);
                            } catch (e) { toast.error(apiErrorMessage(e, "Preview failed")); }
                            finally { setCleanupBusy(false); }
                          }}>
                    {cleanupBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                    Preview duplicates
                  </button>
                </div>
              )}
              {cleanupPreview && !cleanupResult && (
                <div className="space-y-2 pt-1">
                  <div className="border border-border p-3 space-y-1 bg-muted/30">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Preview</div>
                    <div>Chain-level duplicate groups: <span className="font-mono font-semibold">{cleanupPreview.chain_groups}</span> · rows to remove: <span className="font-mono font-semibold">{cleanupPreview.chain_marked}</span></div>
                    <div>Title-level duplicate groups: <span className="font-mono font-semibold">{cleanupPreview.title_groups}</span> · rows to remove: <span className="font-mono font-semibold">{cleanupPreview.title_marked}</span></div>
                    <div className="text-foreground font-semibold mt-1">Total to soft-delete: <span className="font-mono">{cleanupPreview.reminders_marked_for_delete}</span></div>
                  </div>
                  {cleanupPreview.reminders_marked_for_delete === 0 ? (
                    <div className="text-emerald-700 font-medium">Nothing to clean — you're all good!</div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      <div className="text-muted-foreground">
                        Step 2 — Apply the cleanup. This soft-deletes {cleanupPreview.reminders_marked_for_delete} row(s).
                      </div>
                      <button type="button"
                              className="self-start px-3 py-1.5 border border-rose-300 bg-rose-50 dark:bg-rose-500/10 text-rose-800 dark:text-rose-300 hover:bg-rose-100 flex items-center gap-1.5"
                              data-testid="team-reminders-cleanup-apply"
                              disabled={cleanupBusy}
                              onClick={async () => {
                                if (!activeWorkspaceId) return;
                                setCleanupBusy(true);
                                try {
                                  const { data } = await api.post(
                                    `/workspaces/${activeWorkspaceId}/reminders/cleanup`,
                                    { dry_run: false, min_duplicates: 5 });
                                  setCleanupResult(data);
                                  toast.success(`Cleaned ${data.reminders_marked_for_delete} duplicate reminder(s)`);
                                  await load();
                                } catch (e) { toast.error(apiErrorMessage(e, "Cleanup failed")); }
                                finally { setCleanupBusy(false); }
                              }}>
                        {cleanupBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                        Apply cleanup
                      </button>
                    </div>
                  )}
                </div>
              )}
              {cleanupResult && (
                <div className="space-y-2 pt-1">
                  <div className="border border-emerald-300 p-3 space-y-1 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-900 dark:text-emerald-200">
                    <div className="font-semibold">Cleanup complete</div>
                    <div>Chain rows soft-deleted: <span className="font-mono">{cleanupResult.chain_marked}</span></div>
                    <div>Title rows soft-deleted: <span className="font-mono">{cleanupResult.title_marked}</span></div>
                    <div>Total: <span className="font-mono font-semibold">{cleanupResult.reminders_marked_for_delete}</span></div>
                    <div className="text-[10px] opacity-70 mt-1">
                      The pinboard has been refreshed. New duplicates can no longer be created — the
                      database now enforces one open occurrence per recurring chain.
                    </div>
                  </div>
                  <button type="button"
                          className="self-start px-3 py-1.5 border border-border hover:bg-muted"
                          data-testid="team-reminders-cleanup-close"
                          onClick={() => setCleanupOpen(false)}>
                    Close
                  </button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}


      <DigestPreviewModal
        open={digestPreviewOpen}
        onClose={() => setDigestPreviewOpen(false)}
      />
    </div>
  );
}

function SummaryCard({ label, value, accent }) {
  const accentCls = accent === "rose"
    ? "text-rose-600"
    : accent === "sky"
      ? "text-sky-600"
      : "text-foreground";
  return (
    <div className="border border-border p-2 sm:p-4">
      <div className="text-[9px] sm:text-[10px] uppercase text-muted-foreground tracking-wide leading-tight line-clamp-2">{label}</div>
      <div className={`text-lg sm:text-2xl font-bold mt-0.5 sm:mt-1 ${accentCls}`}>{value}</div>
    </div>
  );
}

function PinBucket({ bucket, groupBy, docs, currentUserId, isAdmin, onComplete, onEdit, onQuickAdd,
                     wsId, onQuickCreated, quickPreset,
                     collapsed, onToggleCollapsed,
                     selected, onToggleSelected, staleDays }) {
  // R268~39 · Feb 2026 — Register this bucket as a droppable target
  // so a card dragged in from another bucket can be dropped anywhere
  // in the header/body area (not only on an existing item).
  const { setNodeRef: setDroppableRef, isOver } = useDroppable({ id: `bucket-${bucket.id}` });
  const hasOverdue = bucket.overdue > 0;
  const isPersonGroup = groupBy === "owner" || groupBy === "creator" || groupBy === "delegate";
  const isNoneBucket = bucket.id === "__none__";
  const firstItem = bucket.items[0];
  const avatar = isPersonGroup
    ? (groupBy === "delegate" ? firstItem?.delegated_to_avatar : firstItem?.owner_avatar)
    : null;
  const docsChip = docs && docs.total > 0 ? (() => {
    const p = docs.uploaded_pct || 0;
    const cls = p >= 95 ? "border-emerald-500 bg-emerald-50 text-emerald-700"
             : p >= 50 ? "border-amber-500 bg-amber-50 text-amber-800"
             : "border-rose-500 bg-rose-50 text-rose-700";
    const tip = `${docs.uploaded}/${docs.total} uploaded · ${docs.verified}/${docs.total} verified` +
      (docs.expiring_soon ? ` · ${docs.expiring_soon} expiring soon` : "") +
      (docs.expired ? ` · ${docs.expired} expired` : "");
    return (
      <span
        className={`inline-block border px-1 text-[10px] font-mono font-bold ${cls}`}
        title={tip}
        data-testid={`team-owner-docs-pct-${bucket.id}`}
      >
        {p}%
        {docs.expired > 0 && <span className="ml-0.5 text-rose-700">!</span>}
      </span>
    );
  })() : null;

  return (
    <div
      ref={setDroppableRef}
      className={`border ${hasOverdue ? "border-rose-300" : "border-border"} bg-background flex flex-col ${isNoneBucket ? "opacity-90 border-dashed" : ""} ${isOver ? "ring-2 ring-primary/60 bg-primary/5" : ""}`}
      data-testid={`team-bucket-${groupBy}-${bucket.id}`}
    >
      <div className={`px-3 py-2.5 border-b flex items-center gap-2 ${hasOverdue ? "border-rose-200 bg-rose-50/50" : "border-border bg-muted/40"}`}>
        {/* R268~22 · Feb 2026 — Chevron toggles this bucket's collapsed
            state. Same visual language as DCR groups so users don't
            have to relearn the affordance. */}
        <button
          type="button"
          onClick={onToggleCollapsed}
          className="shrink-0 -ml-1 p-1 text-muted-foreground hover:text-foreground"
          title={collapsed ? "Expand bucket" : "Collapse bucket"}
          aria-expanded={!collapsed}
          data-testid={`team-bucket-toggle-${groupBy}-${bucket.id}`}
        >
          {collapsed
            ? <ChevronRight className="w-3.5 h-3.5" />
            : <ChevronDown  className="w-3.5 h-3.5" />}
        </button>
        {isPersonGroup && !isNoneBucket && (
          <Initials name={bucket.label} avatar={avatar} size={7} />
        )}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold truncate flex items-center gap-1.5">
            <span className="truncate">{bucket.label}</span>
            {docsChip}
          </div>
          <div className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-2">
            <span>{bucket.items.length} {bucket.items.length === 1 ? "item" : "items"}</span>
            {hasOverdue && (
              <span className="text-rose-600 font-medium flex items-center gap-1">
                <AlertTriangle className="w-2.5 h-2.5" /> {bucket.overdue} overdue
              </span>
            )}
          </div>
        </div>
        {onQuickAdd && (
          <button
            type="button"
            onClick={onQuickAdd}
            className="shrink-0 text-[11px] px-1.5 py-1 border border-border bg-background hover:border-primary/60 hover:text-primary flex items-center gap-1"
            title={`Add a reminder to ${bucket.label}`}
            data-testid={`team-bucket-quickadd-${groupBy}-${bucket.id}`}
          >
            <Plus className="w-3 h-3" /> Add
          </button>
        )}
      </div>
      {!collapsed && (
      <div className="divide-y divide-border max-h-[26rem] overflow-y-auto">
        {wsId && quickPreset && (
          <QuickAddInline
            wsId={wsId}
            preset={quickPreset}
            testid={`team-bucket-quickadd-inline-${groupBy}-${bucket.id}`}
            onCreated={onQuickCreated}
          />
        )}
        <SortableContext items={bucket.items.map((r) => r.id)} strategy={verticalListSortingStrategy}>
          {bucket.items.map((r, idx) => (
            <SortableTeamRow
              key={r.id}
              r={r}
              groupBy={groupBy}
              currentUserId={currentUserId}
              isAdmin={isAdmin}
              onComplete={onComplete}
              onEdit={onEdit}
              onReloadRequested={onQuickCreated}
              selected={selected}
              onToggleSelected={onToggleSelected}
              staleDays={staleDays}
              rowIndex={idx + 1}
            />
          ))}
        </SortableContext>
      </div>
      )}
    </div>
  );
}

function SortableTeamRow({ r, groupBy, currentUserId, isAdmin, onComplete, onEdit, onReloadRequested, selected, onToggleSelected, staleDays, rowIndex }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: r.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 10 : "auto",
  };
  return (
    <div ref={setNodeRef} style={style} data-testid={`sortable-reminder-${r.id}`}>
      <TeamRow
        r={r}
        groupBy={groupBy}
        currentUserId={currentUserId}
        isAdmin={isAdmin}
        onComplete={onComplete}
        onEdit={onEdit}
        onReloadRequested={onReloadRequested}
        dragHandleProps={{ ...attributes, ...listeners }}
        selected={selected}
        onToggleSelected={onToggleSelected}
        staleDays={staleDays}
        rowIndex={rowIndex}
      />
    </div>
  );
}

function TeamRow({ r, groupBy, currentUserId, isAdmin, onComplete, onEdit, onReloadRequested, dragHandleProps, selected, onToggleSelected, staleDays, rowIndex }) {
  const priChip = PRI_CHIP[r.priority] || PRI_CHIP.normal;
  const now = new Date().toISOString();
  const isSnoozed = r.snooze_until && r.snooze_until > now;
  const isPrivate = !!r.is_private;
  const done = r.status === "done";
  // Only owner or delegatee can edit / complete — mirrors the backend
  // permission gate in _can_edit(). Everyone else sees a read-only row.
  // R268~29 · Feb 2026 — Admins can edit/complete anyone's reminder.
  // Everyone else needs to own or be delegated the reminder. Mirrors the
  // backend permission gate in _can_edit().
  const canAct = !done && (isAdmin || r.owner_id === currentUserId || r.delegated_to === currentUserId);
  // Hide the chip that matches the current grouping to reduce visual noise.
  const showChip = {
    owner: groupBy !== "owner" && groupBy !== "creator",
    delegate: groupBy !== "delegate" && !!r.delegated_to_name,
    client: groupBy !== "client" && !!r.client_name,
    brand: groupBy !== "brand" && !!r.brand_name,
    project: groupBy !== "project" && !!r.project_name,
    task: !!r.task_id && !!r.linked_entity && r.linked_entity.kind === "task",
    priority: groupBy !== "priority",
  };

  return (
    <div
      className={`px-3 py-2.5 ${isPrivate ? "bg-amber-50/60 dark:bg-amber-950/20 border-l-2 border-l-amber-500" : ""} ${done ? "opacity-60" : ""}`}
      data-testid={`team-row-${r.id}`}
      data-private={isPrivate ? "true" : "false"}
    >
      <div className="flex items-start gap-2">
        {/* R268~32 · Feb 2026 — Bulk-complete checkbox (admin only,
            hidden on done rows). Sits before the drag handle so it's
            the first-tap target. */}
        {isAdmin && !done && onToggleSelected && (
          <input
            type="checkbox"
            checked={selected?.has(r.id) || false}
            onChange={() => onToggleSelected(r.id)}
            className="mt-1 shrink-0 accent-primary"
            aria-label="Select for bulk actions"
            data-testid={`team-row-select-${r.id}`}
          />
        )}
        {dragHandleProps && (
          <button
            type="button"
            {...dragHandleProps}
            className="mt-0.5 -ml-1 p-1 text-muted-foreground/60 hover:text-muted-foreground cursor-grab active:cursor-grabbing touch-none"
            aria-label="Drag to reorder"
            data-testid={`drag-handle-${r.id}`}
            title="Drag to reorder"
          >
            <GripVertical className="w-3.5 h-3.5" />
          </button>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            {rowIndex != null && (
              <span
                className="text-[10px] font-mono tabular-nums text-muted-foreground/70 shrink-0 select-none min-w-[1.4rem] text-right"
                aria-hidden
                title={`Row ${rowIndex}`}
                data-testid={`team-row-index-${r.id}`}
              >
                {rowIndex}.
              </span>
            )}
            {isPrivate && (
              <span
                className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide bg-amber-500 text-white border border-amber-600 shrink-0"
                title="Owner marked as Only me — only admins & the owner see this."
              >
                <Lock className="w-2.5 h-2.5" /> Only me
              </span>
            )}
            <span className={`text-[13px] font-semibold ${done ? "line-through text-muted-foreground" : "text-foreground"}`} data-testid={`team-row-title-${r.id}`}>{r.title}</span>
            {/* R268~29 · Feb 2026 — Age sticker: shows "3d", "12h", etc.
                Red when overdue so managers spot stale-and-overdue in a
                single glance. Emerald when done. */}
            {(() => {
              const age = ageChipText(r.created_at);
              if (!age) return null;
              // R268~33 · Feb 2026 — Amber tone when created > staleDays
              // ago and the reminder is still open (but not yet
              // overdue). Overdue always wins with red.
              let ageDays = null;
              if (r.created_at) {
                const t = new Date(r.created_at).getTime();
                if (Number.isFinite(t)) ageDays = (Date.now() - t) / 86400000;
              }
              const isStale = !done && !r._overdue && ageDays != null && staleDays != null && ageDays >= staleDays;
              const tone = done
                ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                : r._overdue
                  ? "bg-rose-500 text-white border-rose-600"
                  : isStale
                    ? "bg-amber-100 text-amber-800 border-amber-400"
                    : "bg-slate-50 text-slate-600 border-slate-300";
              const title = r._overdue
                ? `Overdue · created ${fmtRelative(r.created_at)}`
                : isStale
                  ? `Stale · older than ${staleDays}d threshold · created ${fmtRelative(r.created_at)}`
                  : `Created ${fmtRelative(r.created_at)}`;
              return (
                <span
                  className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 border text-[9px] font-semibold uppercase tracking-widest shrink-0 ${tone}`}
                  title={title}
                  data-testid={`team-row-age-${r.id}`}
                >
                  {age}
                </span>
              );
            })()}
            {r.recurrence && (
              <span className="inline-flex items-center gap-0.5 text-[9px] text-muted-foreground" title={`Repeats ${r.recurrence}`}>
                <Repeat className="w-2.5 h-2.5" /> {r.recurrence}
              </span>
            )}
          </div>
          {r.note && (
            <div className="text-[10px] text-muted-foreground mt-0.5 line-clamp-2">{r.note}</div>
          )}
          {done && r.response_note && (
            // R268~4 · Feb 2026 — Surface the response note captured
            // at complete-time so managers can scan the audit trail
            // without opening each edit dialog. Renders as a compact
            // emerald-tinted strip; the closed_by_name enrichment
            // gives us "by <person> · <relative>" for context.
            <div
              className="mt-1 px-2 py-1 bg-emerald-50 dark:bg-emerald-950/30 border-l-2 border-l-emerald-400 text-[10px] text-emerald-900 dark:text-emerald-100 leading-snug"
              data-testid={`team-row-response-${r.id}`}
              title={r.response_note}
            >
              <div className="flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300 mb-0.5">
                <Check className="w-2.5 h-2.5" /> Response
                {r.closed_by_name && <span className="font-normal normal-case tracking-normal text-emerald-700/70 dark:text-emerald-300/70">· by {r.closed_by_name}</span>}
                {r.closed_at && <span className="font-normal normal-case tracking-normal text-emerald-700/70 dark:text-emerald-300/70">· {fmtRelative(r.closed_at)}</span>}
              </div>
              <div className="line-clamp-3 whitespace-pre-wrap break-words">{r.response_note}</div>
            </div>
          )}
          <div className="text-[10px] mt-1 flex items-center gap-1.5 flex-wrap">
            {showChip.priority && (
              <span className={`px-1.5 py-0.5 border ${priChip}`}>{r.priority || "normal"}</span>
            )}
            {r.remind_at && (
              <span className={`inline-flex items-center gap-1 ${r._overdue ? "text-rose-600 font-medium" : "text-muted-foreground"}`}>
                <Clock className="w-2.5 h-2.5" /> {fmtRelative(r.remind_at)}
              </span>
            )}
            {isSnoozed && (
              <span className="text-amber-600">snoozed → {fmtRelative(r.snooze_until)}</span>
            )}
            {showChip.owner && r.owner_name && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-muted/60 text-muted-foreground border border-border" title={`Created by ${r.owner_name}`}>
                <User className="w-2.5 h-2.5" /> by {r.owner_name}
              </span>
            )}
            {showChip.delegate && r.delegated_to_name && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-300" title={`Delegated to ${r.delegated_to_name}`}>
                → {r.delegated_to_name}
              </span>
            )}
            {showChip.client && r.client_name && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-sky-50 text-sky-700 border border-sky-300">
                <Building2 className="w-2.5 h-2.5" /> {r.client_name}
              </span>
            )}
            {showChip.brand && r.brand_name && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-300">
                <Package className="w-2.5 h-2.5" /> {r.brand_name}
              </span>
            )}
            {showChip.project && r.project_name && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-300">
                <Briefcase className="w-2.5 h-2.5" /> {r.project_name}
              </span>
            )}
            {showChip.task && (
              <Link
                to={r.linked_entity?.href || "#"}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-primary/10 text-primary border border-primary/30 hover:bg-primary/20 transition"
                title={`Task: ${r.linked_entity?.name}`}
              >
                <ListChecks className="w-2.5 h-2.5" />
                <span className="truncate max-w-[160px]">{r.linked_entity?.name}</span>
              </Link>
            )}
            {r.linked_entity && r.linked_entity.href && r.linked_entity.kind !== "task" && !showChip[r.linked_entity.kind] && (
              <Link
                to={r.linked_entity.href}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-primary/10 text-primary border border-primary/30 hover:bg-primary/20"
              >
                <Link2 className="w-2.5 h-2.5" />
                <span className="truncate max-w-[140px]">{r.linked_entity.name}</span>
              </Link>
            )}
          </div>
        </div>
        {canAct && (
          <div className="flex flex-col items-end gap-1 shrink-0" data-testid={`team-row-actions-${r.id}`}>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onComplete && onComplete(r); }}
              className="p-1 border border-transparent hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-700"
              title="Mark done"
              data-testid={`team-row-complete-${r.id}`}
            >
              <Check className="w-3.5 h-3.5" />
            </button>
            {/* R268~18 · Feb 2026 — Snooze menu on team cards, mirroring
                the header reminders popup. Owner / delegate only
                (already gated by `canAct`). */}
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  onClick={(e) => e.stopPropagation()}
                  className="p-1 border border-transparent hover:bg-muted hover:border-border"
                  title="Snooze"
                  data-testid={`team-row-snooze-${r.id}`}
                >
                  <MoonStar className="w-3.5 h-3.5" />
                </button>
              </PopoverTrigger>
              <PopoverContent side="left" align="start" className="w-40 p-1 rounded-none" onClick={(e) => e.stopPropagation()}>
                {[
                  { mins: 15, label: "15 min" },
                  { mins: 60, label: "1 hr" },
                  { mins: 60 * 4, label: "4 hrs" },
                  { mins: 60 * 24, label: "Tomorrow" },
                  { mins: 60 * 24 * 7, label: "Next week" },
                ].map((opt) => (
                  <button
                    key={opt.mins}
                    type="button"
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        const remindAt = new Date(Date.now() + opt.mins * 60 * 1000).toISOString();
                        await api.patch(`/reminders/${r.id}`, { remind_at: remindAt });
                        toast.success(`Snoozed · ${opt.label}`);
                        onReloadRequested && onReloadRequested();
                      } catch (err) { toast.error(apiErrorMessage(err, "Snooze failed")); }
                    }}
                    className="w-full text-left text-xs px-2 py-1 hover:bg-muted"
                    data-testid={`team-row-snooze-${r.id}-${opt.mins}`}
                  >{opt.label}</button>
                ))}
              </PopoverContent>
            </Popover>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onEdit && onEdit(r.id); }}
              className="p-1 border border-transparent hover:bg-muted hover:border-border"
              title="Edit reminder"
              data-testid={`team-row-edit-${r.id}`}
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
        {done && (
          // R268~4 · Feb 2026 — Done rows still need a way to open the
          // edit dialog so managers can see the full response note +
          // audit metadata (closer / close time). The dialog itself is
          // already read-only for non-owners (canAct gate), so this is
          // safe for every viewer.
          <div className="flex flex-col items-end gap-1 shrink-0" data-testid={`team-row-actions-${r.id}`}>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onEdit && onEdit(r.id); }}
              className="p-1 border border-transparent hover:bg-muted hover:border-border text-muted-foreground"
              title="View reminder & response"
              data-testid={`team-row-view-${r.id}`}
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// R155 · Feb 2026 — Lightweight edit dialog inline on Team Reminders.
// Reuses the same PATCH endpoint as the main panel — just fewer fields
// so the board stays lightweight (title / note / remind_at / priority /
// recurrence). Full edit still available via the floating panel.
function TeamEditDialog({ rid, wsId, currentUserId, isAdmin, onClose, onSaved }) {
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showCompleteDlg, setShowCompleteDlg] = useState(false);
  // R268~31 · Feb 2026 — Load members / clients / brands / projects so
  // the Edit dialog can offer the same delegation + entity dropdowns
  // as TeamNewReminderDialog. Empty arrays when wsId isn't ready yet.
  const [members, setMembers] = useState([]);
  const [clients, setClients] = useState([]);
  const [brands, setBrands] = useState([]);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    let cancelled = false;
    api.get(`/reminders/${rid}`).then(({ data }) => {
      if (!cancelled) setR(data);
    }).catch((e) => { toast.error(apiErrorMessage(e, "Failed to load")); onClose(); });
    return () => { cancelled = true; };
  }, [rid, onClose]);

  useEffect(() => {
    if (!wsId) return;
    let cancelled = false;
    const safe = (p) => p.catch(() => ({ data: [] }));
    Promise.all([
      safe(api.get(`/workspaces/${wsId}/members`)),
      safe(api.get(`/workspaces/${wsId}/clients`)),
      safe(api.get(`/workspaces/${wsId}/brands`)),
      safe(api.get(`/workspaces/${wsId}/projects`)),
    ]).then(([mR, cR, bR, pR]) => {
      if (cancelled) return;
      setMembers(Array.isArray(mR.data) ? mR.data : []);
      setClients(Array.isArray(cR.data) ? cR.data : []);
      setBrands(Array.isArray(bR.data) ? bR.data : []);
      setProjects(Array.isArray(pR.data) ? pR.data : []);
    });
    return () => { cancelled = true; };
  }, [wsId]);

  const brandOpts = useMemo(() => (
    r?.client_id ? brands.filter((b) => b.client_id === r.client_id) : brands
  ), [brands, r?.client_id]);
  const projectOpts = useMemo(() => {
    let out = projects;
    if (r?.client_id) out = out.filter((p) => p.client_id === r.client_id);
    if (r?.brand_id)  out = out.filter((p) => p.brand_id  === r.brand_id);
    return out;
  }, [projects, r?.client_id, r?.brand_id]);

  if (!r) return null;
  const save = async () => {
    setBusy(true);
    try {
      // R268~31 · Persist entity + delegation fields alongside the
      // basics so the Edit dialog matches TeamNewReminderDialog.
      const payload = {
        title: r.title,
        note: r.note,
        priority: r.priority,
        remind_at: r.remind_at,
        recurrence: r.recurrence || "none",
        delegated_to: r.delegated_to || null,
        client_id: r.client_id || null,
        brand_id: r.brand_id || null,
        project_id: r.project_id || null,
        is_private: !!r.is_private,
      };
      await api.patch(`/reminders/${rid}`, payload);
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };
  const complete = () => setShowCompleteDlg(true);
  const del = async () => {
    if (!window.confirm("Delete this reminder?")) return;
    setBusy(true);
    try {
      await api.delete(`/reminders/${rid}`);
      toast.success("Deleted");
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
    finally { setBusy(false); }
  };
  // R268~31 · Admins can edit anyone's reminder — same rule as the
  // Team Reminders row-level `canAct` (matches backend _can_edit()).
  const canAct = isAdmin || r.owner_id === currentUserId || r.delegated_to === currentUserId;
  const toLocalDT = (iso) => { if (!iso) return ""; try { const d = new Date(iso); const pad = (n) => String(n).padStart(2, "0"); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`; } catch { return ""; } };
  return (
    <div className="fixed inset-0 z-[1100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="team-edit-dialog">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-background border border-border shadow-2xl flex flex-col max-h-[92vh]">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between bg-muted/40 shrink-0">
          <h3 className="text-sm font-semibold" data-testid="team-edit-title-heading">
            {r.status === "done" ? "Reminder response" : "Edit reminder"}
          </h3>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground text-lg leading-none">×</button>
        </div>
        <div className="p-4 space-y-2 text-xs overflow-y-auto">
          {r.status === "done" && r.response_note && (
            <div
              className="px-2 py-1.5 bg-emerald-50 dark:bg-emerald-950/30 border-l-2 border-l-emerald-400 text-[11px] text-emerald-900 dark:text-emerald-100 leading-snug"
              data-testid="team-edit-response"
            >
              <div className="flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300 mb-0.5">
                <Check className="w-2.5 h-2.5" /> Response
                {r.closed_by_name && <span className="font-normal normal-case tracking-normal text-emerald-700/70 dark:text-emerald-300/70">· by {r.closed_by_name}</span>}
                {r.closed_at && <span className="font-normal normal-case tracking-normal text-emerald-700/70 dark:text-emerald-300/70">· {new Date(r.closed_at).toLocaleString()}</span>}
              </div>
              <div className="whitespace-pre-wrap break-words">{r.response_note}</div>
            </div>
          )}
          <div>
            <div className="label-eyebrow text-[10px] mb-0.5">Title</div>
            <input value={r.title || ""} onChange={(e) => setR({ ...r, title: e.target.value })} className="w-full h-10 sm:h-9 px-2 border border-border bg-background text-xs" disabled={!canAct} data-testid="team-edit-title" />
          </div>
          <div>
            <div className="label-eyebrow text-[10px] mb-0.5">Note</div>
            <textarea value={r.note || ""} onChange={(e) => setR({ ...r, note: e.target.value })} rows={2} className="w-full text-xs px-2 py-1 border border-border bg-background resize-y" disabled={!canAct} data-testid="team-edit-note" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <div className="label-eyebrow text-[10px] mb-0.5">Remind at</div>
              <input type="datetime-local" value={toLocalDT(r.remind_at)} onChange={(e) => setR({ ...r, remind_at: e.target.value ? new Date(e.target.value).toISOString() : null })} className="w-full h-10 sm:h-8 px-2 border border-border bg-background text-xs" disabled={!canAct} data-testid="team-edit-when" />
            </div>
            <div>
              <div className="label-eyebrow text-[10px] mb-0.5">Priority</div>
              <select value={r.priority || "normal"} onChange={(e) => setR({ ...r, priority: e.target.value })} className="w-full h-10 sm:h-8 px-2 border border-border bg-background text-xs" disabled={!canAct} data-testid="team-edit-priority">
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <div className="label-eyebrow text-[10px] mb-0.5">Repeat</div>
              <select value={r.recurrence || ""} onChange={(e) => setR({ ...r, recurrence: e.target.value })} className="w-full h-10 sm:h-8 px-2 border border-border bg-background text-xs" disabled={!canAct} data-testid="team-edit-recurrence">
                <option value="">Once</option>
                <option value="daily">Every day</option>
                <option value="weekdays">Every weekday</option>
                <option value="weekly">Every week</option>
                <option value="biweekly">Every 2 weeks</option>
                <option value="monthly">Every month</option>
                <option value="monthly_first">First of month</option>
                <option value="monthly_last">Last of month</option>
                <option value="yearly">Every year</option>
              </select>
            </div>
            {/* R268~31 · Feb 2026 — Delegation + entity dropdowns for
                parity with TeamNewReminderDialog + header popup. */}
            <div className="sm:col-span-2">
              <div className="label-eyebrow text-[10px] mb-0.5">Delegate to</div>
              <select value={r.delegated_to || ""} onChange={(e) => setR({ ...r, delegated_to: e.target.value || null })} className="w-full h-10 sm:h-8 px-2 border border-border bg-background text-xs" disabled={!canAct} data-testid="team-edit-delegate">
                <option value="">— No one (owner action)</option>
                {members.filter((m) => m.status !== "removed").map((m) => (
                  <option key={m.user_id || m.id} value={m.user_id || m.id}>
                    {m.name || m.email || m.user_id}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <div className="label-eyebrow text-[10px] mb-0.5">Client</div>
              <select value={r.client_id || ""} onChange={(e) => setR({ ...r, client_id: e.target.value || null, brand_id: null, project_id: null })} className="w-full h-10 sm:h-8 px-2 border border-border bg-background text-xs" disabled={!canAct} data-testid="team-edit-client">
                <option value="">— Any client</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="label-eyebrow text-[10px] mb-0.5">Brand</div>
              <select value={r.brand_id || ""} onChange={(e) => setR({ ...r, brand_id: e.target.value || null, project_id: null })} className="w-full h-10 sm:h-8 px-2 border border-border bg-background text-xs" disabled={!canAct} data-testid="team-edit-brand">
                <option value="">— Any brand</option>
                {brandOpts.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <div className="label-eyebrow text-[10px] mb-0.5">Project</div>
              <select value={r.project_id || ""} onChange={(e) => setR({ ...r, project_id: e.target.value || null })} className="w-full h-10 sm:h-8 px-2 border border-border bg-background text-xs" disabled={!canAct} data-testid="team-edit-project">
                <option value="">— Any project</option>
                {projectOpts.map((pr) => (
                  <option key={pr.id} value={pr.id}>{pr.name}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="flex items-center gap-1.5 text-[11px] cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!r.is_private}
                  onChange={(e) => setR({ ...r, is_private: e.target.checked })}
                  disabled={!canAct}
                  data-testid="team-edit-private"
                />
                Private reminder <span className="text-muted-foreground">(only you can see it)</span>
              </label>
            </div>
          </div>
        </div>
        <div className="px-4 py-3 border-t border-border flex items-center gap-2 bg-muted/20">
          {canAct && (
            <button type="button" onClick={del} disabled={busy} className="text-xs px-2.5 py-1.5 border border-border hover:bg-rose-50 hover:text-rose-700 hover:border-rose-300 mr-auto" data-testid="team-edit-delete">
              <Trash2 className="w-3 h-3 inline mr-1" /> Delete
            </button>
          )}
          {canAct ? (
            <>
              <button type="button" onClick={complete} disabled={busy} className="text-xs px-2.5 py-1.5 border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100" data-testid="team-edit-complete">
                <Check className="w-3 h-3 inline mr-1" /> Mark done
              </button>
              <button type="button" onClick={save} disabled={busy} className="text-xs px-3 py-1.5 bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 font-medium" data-testid="team-edit-save">
                {busy ? "Saving…" : "Save"}
              </button>
            </>
          ) : (
            <span className="text-[11px] text-muted-foreground italic">View-only — only owner or delegatee can edit.</span>
          )}
        </div>
      </div>
      {showCompleteDlg && (
        <CompleteReminderDialog
          reminder={r}
          onClose={() => setShowCompleteDlg(false)}
          onDone={() => { setShowCompleteDlg(false); onSaved(); }}
        />
      )}
    </div>
  );
}


// R268~1 · Feb 2026 — Inline quick-add row rendered inside each PinBucket.
// Single-line input; Enter creates the reminder using the bucket's preset
// (delegated_to / client_id / brand_id / project_id / priority).
function QuickAddInline({ wsId, preset = {}, onCreated, testid }) {
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const t = title.trim();
    if (!t || busy) return;
    setBusy(true);
    try {
      // R268~18 · Feb 2026 — Only send keys that have real values;
      // some collections rejected nullable presets when the whole
      // dict was posted, so the quick-add silently 422'd.
      const payload = { title: t, priority: preset.priority || "normal" };
      for (const k of ["delegated_to", "client_id", "brand_id", "project_id", "task_id", "department"]) {
        if (preset[k]) payload[k] = preset[k];
      }
      const { data } = await api.post(`/workspaces/${wsId}/reminders`, payload);
      setTitle("");
      toast.success("Reminder added");
      onCreated?.(data);
      try { window.dispatchEvent(new CustomEvent("reminders:changed")); } catch { /* ignore */ }
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't add reminder"));
    } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-1 px-2 py-1.5 bg-muted/30" data-testid={testid}>
      <Plus className="w-3 h-3 text-muted-foreground shrink-0" />
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
        placeholder="Quick add — type & press Enter"
        disabled={busy}
        className="flex-1 h-7 text-xs bg-background border border-border px-2 focus:outline-none focus:border-primary"
        data-testid={`${testid}-input`}
      />
      <button
        type="button"
        onClick={submit}
        disabled={busy || !title.trim()}
        className="text-[10px] px-2 py-1 border border-primary bg-primary text-primary-foreground disabled:opacity-40"
        data-testid={`${testid}-submit`}
      >
        {busy ? "…" : "Add"}
      </button>
    </div>
  );
}

// R268~1 · Feb 2026 — Standalone "New reminder" dialog. Reused by the
// header button and by each bucket's Add button (which passes a preset).
function TeamNewReminderDialog({ wsId, preset = null, onClose, onCreated }) {
  const p = preset || {};
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [priority, setPriority] = useState(p.priority || "normal");
  const [remindAt, setRemindAt] = useState("");
  const [recurrence, setRecurrence] = useState("");
  const [delegatedTo, setDelegatedTo] = useState(p.delegated_to || "");
  const [clientId, setClientId] = useState(p.client_id || "");
  const [brandId, setBrandId] = useState(p.brand_id || "");
  const [projectId, setProjectId] = useState(p.project_id || "");
  const [isPrivate, setIsPrivate] = useState(false);
  const [busy, setBusy] = useState(false);
  // R268~17 · Feb 2026 — Match the header reminders popup: pull
  // members / clients / brands / projects lookups so the user can
  // pick any relation, not just what the bucket preset dictates.
  const [members, setMembers] = useState([]);
  const [clients, setClients] = useState([]);
  const [brands, setBrands] = useState([]);
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    if (!wsId) return; // R268~19 · Guard against cold-load with stale wsId — dropdowns were showing empty
    let cancelled = false;
    const safe = (p) => p.catch(() => ({ data: [] }));
    Promise.all([
      safe(api.get(`/workspaces/${wsId}/members`)),
      safe(api.get(`/workspaces/${wsId}/clients`)),
      safe(api.get(`/workspaces/${wsId}/brands`)),
      safe(api.get(`/workspaces/${wsId}/projects`)),
    ]).then(([mR, cR, bR, pR]) => {
      if (cancelled) return;
      setMembers(Array.isArray(mR.data) ? mR.data : (mR.data?.rows || []));
      setClients(Array.isArray(cR.data) ? cR.data : (cR.data?.rows || []));
      setBrands(Array.isArray(bR.data) ? bR.data : (bR.data?.rows || []));
      setProjects(Array.isArray(pR.data) ? pR.data : (pR.data?.rows || []));
    });
    return () => { cancelled = true; };
  }, [wsId]);
  // Filter brands / projects by picked client so cascading picks
  // stay coherent (same behaviour as the header popup).
  const brandOpts = clientId ? brands.filter((b) => b.client_id === clientId) : brands;
  const projectOpts = clientId ? projects.filter((pr) => pr.client_id === clientId)
                     : (brandId ? projects.filter((pr) => pr.brand_id === brandId) : projects);
  const submit = async () => {
    const t = title.trim();
    if (!t) { toast.error("Title required"); return; }
    setBusy(true);
    try {
      const payload = {
        title: t,
        note: note.trim() || null,
        priority,
        remind_at: remindAt ? new Date(remindAt).toISOString() : null,
        recurrence: recurrence || null,
        delegated_to: delegatedTo || null,
        client_id: clientId || null,
        brand_id: brandId || null,
        project_id: projectId || null,
        task_id: p.task_id || null,
        department: p.department || null,
      };
      if (isPrivate) payload.is_private = true;
      const { data } = await api.post(`/workspaces/${wsId}/reminders`, payload);
      toast.success("Reminder added");
      onCreated?.(data);
      try { window.dispatchEvent(new CustomEvent("reminders:changed")); } catch { /* ignore */ }
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't add reminder"));
    } finally { setBusy(false); }
  };
  return (
    // R268~17 · Feb 2026 — Full-height sheet on phones, centered card
    // on desktop. Content scrolls internally so the sticky footer
    // (Cancel / Add reminder) is always reachable.
    <div className="fixed inset-0 z-[1100] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4" onClick={onClose} data-testid="team-new-dialog">
      <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-md bg-background border border-border shadow-2xl max-h-[92vh] flex flex-col">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between bg-muted/40 shrink-0">
          <h3 className="text-sm font-semibold">New reminder</h3>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground text-lg leading-none p-1 -mr-1">×</button>
        </div>
        <div className="p-4 space-y-2 text-xs overflow-y-auto flex-1">
          {preset?.bucketLabel && (
            <div className="text-[11px] text-muted-foreground italic">
              Scoped to: <span className="text-foreground font-semibold">{preset.bucketLabel}</span>
            </div>
          )}
          <div>
            <div className="label-eyebrow text-[10px] mb-0.5">Title</div>
            <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(); }} className="w-full h-9 px-2 border border-border bg-background" data-testid="team-new-title" />
          </div>
          <div>
            <div className="label-eyebrow text-[10px] mb-0.5">Note</div>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="w-full text-xs px-2 py-1 border border-border bg-background resize-y" data-testid="team-new-note" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <div className="label-eyebrow text-[10px] mb-0.5">Remind at</div>
              <input type="datetime-local" value={remindAt} onChange={(e) => setRemindAt(e.target.value)} className="w-full h-10 sm:h-8 px-2 border border-border bg-background text-xs" data-testid="team-new-when" />
            </div>
            <div>
              <div className="label-eyebrow text-[10px] mb-0.5">Priority</div>
              <select value={priority} onChange={(e) => setPriority(e.target.value)} className="w-full h-10 sm:h-8 px-2 border border-border bg-background text-xs" data-testid="team-new-priority">
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <div className="label-eyebrow text-[10px] mb-0.5">Repeat</div>
              <select value={recurrence} onChange={(e) => setRecurrence(e.target.value)} className="w-full h-10 sm:h-8 px-2 border border-border bg-background text-xs" data-testid="team-new-recurrence">
                <option value="">Once</option>
                <option value="daily">Every day</option>
                <option value="weekdays">Every weekday</option>
                <option value="weekly">Every week</option>
                <option value="biweekly">Every 2 weeks</option>
                <option value="monthly">Every month</option>
                <option value="monthly_first">First of month</option>
                <option value="monthly_last">Last of month</option>
                <option value="yearly">Every year</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <div className="label-eyebrow text-[10px] mb-0.5">Delegate to</div>
              <select value={delegatedTo} onChange={(e) => setDelegatedTo(e.target.value)} className="w-full h-10 sm:h-8 px-2 border border-border bg-background text-xs" data-testid="team-new-delegate">
                <option value="">— No one (owner action)</option>
                {members.filter((m) => m.status !== "removed").map((m) => (
                  <option key={m.user_id || m.id} value={m.user_id || m.id}>
                    {m.name || m.email || m.user_id}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <div className="label-eyebrow text-[10px] mb-0.5">Client</div>
              <select value={clientId} onChange={(e) => { setClientId(e.target.value); setBrandId(""); setProjectId(""); }} className="w-full h-10 sm:h-8 px-2 border border-border bg-background text-xs" data-testid="team-new-client">
                <option value="">— Any client</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="label-eyebrow text-[10px] mb-0.5">Brand</div>
              <select value={brandId} onChange={(e) => { setBrandId(e.target.value); setProjectId(""); }} className="w-full h-10 sm:h-8 px-2 border border-border bg-background text-xs" data-testid="team-new-brand">
                <option value="">— Any brand</option>
                {brandOpts.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <div className="label-eyebrow text-[10px] mb-0.5">Project</div>
              <select value={projectId} onChange={(e) => setProjectId(e.target.value)} className="w-full h-10 sm:h-8 px-2 border border-border bg-background text-xs" data-testid="team-new-project">
                <option value="">— Any project</option>
                {projectOpts.map((pr) => (
                  <option key={pr.id} value={pr.id}>{pr.name}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="flex items-center gap-1.5 text-[11px] cursor-pointer">
                <input type="checkbox" checked={isPrivate} onChange={(e) => setIsPrivate(e.target.checked)} data-testid="team-new-private" />
                Private reminder <span className="text-muted-foreground">(only you can see it)</span>
              </label>
            </div>
          </div>
        </div>
        <div className="px-4 py-3 border-t border-border flex items-center gap-2 bg-muted/20 shrink-0">
          <button type="button" onClick={onClose} className="text-xs px-3 py-2 border border-border hover:bg-muted mr-auto" data-testid="team-new-cancel">
            Cancel
          </button>
          <button type="button" onClick={submit} disabled={busy || !title.trim()} className="text-xs px-4 py-2 bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 font-medium" data-testid="team-new-submit">
            {busy ? "Adding…" : "Add reminder"}
          </button>
        </div>
      </div>
    </div>
  );
}
